import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  Mail,
  Linkedin,
  FileText,
  Award,
  BookOpen,
  Briefcase,
  GraduationCap,
  Shield,
  Lightbulb,
  ExternalLink
} from 'lucide-react'

// Leadership team data
const leadershipTeam = [
  {
    id: 'daniel-shields',
    name: 'Daniel Shields',
    title: 'Founder & Chief Technology Officer',
    subtitle: 'Enterprise Cloud Systems Architect',
    email: 'dshields@synthetiq.io',
    image: '/team/daniel_s_graffiti.png',
    featured: true,
  }
]

// Daniel Shields detailed profile
const danielShieldsProfile = {
  summary: `Daniel Shields brings over two decades of experience architecting enterprise-scale cloud systems, data platforms, and machine learning integrations for Fortune 500 companies and global organizations. His expertise spans complex control systems, compliance-by-design architectures, and the strategic deployment of AI/ML solutions across financial services, healthcare, entertainment, and consumer goods sectors.`,
  
  vision: `At Mimeograph Holdings, Daniel leads the technical vision for SynthetIQ Signals® and the Graffiti™ platform—driven by the belief that powerful AI should be accessible to everyone, regardless of socioeconomic status, while maintaining absolute privacy and security standards. His decades of experience with enterprise compliance frameworks directly informs Graffiti™'s privacy-first architecture.`,
  
  expertise: [
    'Azure & AWS Kubernetes Stack Architecture',
    'Scalable Data Science Platforms',
    'ML/AI Pipeline Integration (CI/CD)',
    'Enterprise Data Fabric Design',
    'Privacy & Compliance Architecture',
    'Customer Data Platform (CDP) Design',
    'Real-time ETL & Data Processing',
  ],
  
  compliance: [
    'PII/PHI Protection',
    'HIPAA',
    'ISO 27000+',
    'ISO 9000+',
    'GDPR/CCPA/NY SHIELD',
    'CASL',
    'CAN-SPAM',
    'PCI-DSS',
    'SOC II',
  ],
  
  experience: [
    {
      company: 'PricewaterhouseCoopers LLC',
      location: 'Hallandale Beach, Florida',
      title: 'Senior Manager, Platform Architect',
      period: '2019 – 2025',
      projects: [
      'Customer Link [Lead] | AI Enhanced CDP and Data Fabric for multi-national finance and tax services.',
      'Outcomes Hub [Lead] | Agentic AI Pipeline for variable case-based ETL and RAG collection management.'
      ],
      highlights: [
        'Architected Azure and AWS Kubernetes stack for scalable data science and diverse model applications',
        'Designed Consumer Portfolio Assembly Pattern managing 300 million individuals across 66,000 attributes',
        'Secured $20 million investment for platform design and go-to-market execution',
        'Re-factored legacy monolithic applications to fully portable CI/CD supply chain architecture',
        'Implemented PCI/PII tokenization and policy-based compliance coverage',
      ]
    },
    {
      company: 'Newell Brands',
      location: 'Boca Raton, Florida',
      title: 'Architect: CDP & MarTech Solution Design',
      period: '2018',
      highlights: [
        'Designed customer data platform (CDP) automation across 39 global brands',
        'Developed PRETL library processing 100 million records daily',
        'Implemented W3C CEDDL Standard for client-side data collection',
        'Built compliance controls for CASL and GDPR regional requirements',
      ]
    },
    {
      company: 'Decision Data Corporation',
      location: 'Fort Lauderdale, Florida',
      title: 'Director: Enterprise Data Sciences',
      period: '2008 – Present',
      highlights: [
        'Provides strategic support and technical implementation for web measurement solutions',
        'Serves clients including American Apparel, TD Ameritrade, Kohler, J.Crew, AT&T, GSK, and Sprint',
      ]
    },
    {
      company: 'Dentsu Aegis (Cardinal Path)',
      location: 'Vancouver, BC, Canada',
      title: 'Digital Transformation Architect',
      period: '2012 – 2016',
      highlights: [
        'Led digital transformation initiatives for J.P. Morgan Chase, Papa Johns, American Red Cross, and Merck',
      ]
    },
    {
      company: 'Ernst & Young (Society Consulting)',
      location: 'Seattle, Washington',
      title: 'Principal Digital Analytics Consultant',
      period: '2013 – 2015',
      highlights: [
        'Consulted for Nintendo of America, Microsoft Xbox, NBC Universal, and Bosch',
      ]
    },
  ],
  
  patents: [
    {
      title: 'Systems and Methods to Treat Complex Revenue Cycle Management with Machine Learning in a Standards-Based Electronic Data Interchange',
      applications: ['US 16/166,151', 'US 17/131,269'],
      status: 'First Action: March 2022',
      description: 'A computer system performing analysis and applying multiple machine learning algorithms to predict cost reimbursement probability, billable line items, and recommend transaction code inputs.',
    },
    {
      title: 'System and Method for Dynamically Customizing Web Page Content',
      applications: ['US 20090106237'],
      description: 'Method for providing customized web page content by processing search terms from referring URLs to produce personalized user experiences.',
    },
    {
      title: "System and Method for Tracking a User's Navigation and Enabling Service Representative Replication",
      applications: ['US 20090182643'],
      description: 'System for tracking and replicating user navigation on websites, enabling customer service representatives to understand and assist with user journeys.',
    },
  ],
  
  publications: [
    {
      title: 'Website Optimization: Speed, Search Engine & Conversion Rate Secrets',
      publisher: "O'Reilly Media",
      year: '2008',
      role: 'Contributing Author',
      isbn: '978-0596515089',
    },
    {
      title: 'WebAnalyticsDemystified.com',
      year: '2007 – 2008',
      role: 'Contributing Author',
      description: 'Invited contributor alongside Eric T. Peterson and Judah Phillips on web analytics practice.',
    },
  ],
  
  education: [
    {
      institution: 'Florida Atlantic University',
      location: 'Boca Raton, FL',
      degree: 'B.G.S., Communication & History',
      period: '1999 – 2002',
    },
    {
      institution: 'University of Scranton',
      location: 'Scranton, PA',
      focus: 'Communication & Computer Science',
      period: '1997 – 1999',
      honors: "Dean's List Honors (Spring 1999)",
    },
  ],
}

export default function LeadershipPage() {
  return (
    <div className="pt-24 pb-16 min-h-screen">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-synth-950/30 via-void-950 to-purple-950/20 -z-10" />
      <div className="fixed inset-0 grid-overlay opacity-20 -z-10" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link 
          to="/corporate" 
          className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Corporate
        </Link>
        
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
            Leadership <span className="text-gradient">Team</span>
          </h1>
          <p className="text-xl text-void-400 max-w-3xl">
            Meet the visionaries building the future of accessible, privacy-first artificial intelligence.
          </p>
        </motion.div>
        
        {/* Daniel Shields Profile */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-8"
        >
          {/* Profile Header */}
          <div className="p-8 rounded-3xl bg-void-900/50 border border-void-800/50">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {/* Headshot */}
              <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-synth-600 to-purple-700 shrink-0">
                <img 
                  src="/team/daniel_s_graffiti.png" 
                  alt="Daniel Shields" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="flex-1">
                <h2 className="text-3xl font-display font-bold text-void-50 mb-1">
                  Daniel Shields
                </h2>
                <p className="text-xl text-synth-400 mb-2">Founder & Chief Technology Officer</p>
                <p className="text-void-400 mb-4">Enterprise Cloud Systems Architect</p>
                
                <div className="flex flex-wrap gap-3">
                  <a 
                    href="mailto:dshields@synthetiq.io"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-void-800/80 text-void-300 hover:text-void-100 hover:bg-void-700/80 transition-colors text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    dshields@synthetiq.io
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/trulyDanielWilliam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-void-800/80 text-void-300 hover:text-void-100 hover:bg-void-700/80 transition-colors text-sm"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Summary & Vision */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
              <h3 className="text-lg font-display font-semibold text-void-100 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-synth-400" />
                Background
              </h3>
              <p className="text-void-300 leading-relaxed">
                {danielShieldsProfile.summary}
              </p>
            </div>
            
            <div className="p-6 rounded-2xl bg-gradient-to-br from-synth-900/30 to-purple-900/20 border border-synth-800/30">
              <h3 className="text-lg font-display font-semibold text-void-100 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-synth-400" />
                Vision for Graffiti&trade;
              </h3>
              <p className="text-void-300 leading-relaxed">
                {danielShieldsProfile.vision}
              </p>
            </div>
          </div>
          
          {/* Core Expertise */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-synth-400" />
              Core Expertise
            </h3>
            <div className="flex flex-wrap gap-2">
              {danielShieldsProfile.expertise.map((skill) => (
                <span 
                  key={skill}
                  className="px-3 py-1.5 rounded-lg bg-synth-900/50 text-synth-300 text-sm border border-synth-800/30"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
          
          {/* Compliance Standards */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Compliance & Security Standards
            </h3>
            <div className="flex flex-wrap gap-2">
              {danielShieldsProfile.compliance.map((standard) => (
                <span 
                  key={standard}
                  className="px-3 py-1.5 rounded-lg bg-purple-900/30 text-purple-300 text-sm border border-purple-800/30"
                >
                  {standard}
                </span>
              ))}
            </div>
          </div>
          
          {/* Career Highlights */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-6 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-synth-400" />
              Career Highlights
            </h3>
            
            <div className="space-y-6">
              {danielShieldsProfile.experience.map((role, index) => (
                <div 
                  key={index}
                  className={`relative pl-6 ${index !== danielShieldsProfile.experience.length - 1 ? 'pb-6 border-l border-void-700/50' : ''}`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-0 w-3 h-3 rounded-full bg-synth-500 -translate-x-1.5" />
                  
                  <div className="mb-2">
                    <h4 className="text-void-100 font-semibold">{role.title}</h4>
                    <p className="text-synth-400 text-sm">{role.company}</p>
                    <p className="text-void-500 text-sm">{role.location} • {role.period}</p>
                  </div>
                  
                  <ul className="space-y-1.5">
                    {role.highlights.map((highlight, hIndex) => (
                      <li key={hIndex} className="text-void-400 text-sm flex items-start gap-2">
                        <span className="text-synth-500 mt-1.5">•</span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          
          {/* Patents */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-ember-400" />
              Patents
            </h3>
            
            <div className="space-y-4">
              {danielShieldsProfile.patents.map((patent, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-xl bg-void-800/30 border border-void-700/30"
                >
                  <h4 className="text-void-100 font-medium mb-2">{patent.title}</h4>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {patent.applications.map((app) => (
                      <span key={app} className="text-xs px-2 py-1 rounded bg-ember-900/30 text-ember-300 border border-ember-800/30">
                        {app}
                      </span>
                    ))}
                    {patent.status && (
                      <span className="text-xs px-2 py-1 rounded bg-emerald-900/30 text-emerald-300 border border-emerald-800/30">
                        {patent.status}
                      </span>
                    )}
                  </div>
                  <p className="text-void-400 text-sm">{patent.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Publications */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-signal-400" />
              Publications
            </h3>
            
            <div className="space-y-4">
              {danielShieldsProfile.publications.map((pub, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-xl bg-void-800/30 border border-void-700/30"
                >
                  <h4 className="text-void-100 font-medium mb-1">{pub.title}</h4>
                  <p className="text-signal-400 text-sm mb-1">{pub.role}</p>
                  {pub.publisher && (
                    <p className="text-void-500 text-sm">{pub.publisher} • {pub.year}</p>
                  )}
                  {pub.isbn && (
                    <p className="text-void-500 text-xs mt-1">ISBN: {pub.isbn}</p>
                  )}
                  {pub.description && (
                    <p className="text-void-400 text-sm mt-2">{pub.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Education */}
          <div className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50">
            <h3 className="text-lg font-display font-semibold text-void-100 mb-6 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-synth-400" />
              Education
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {danielShieldsProfile.education.map((edu, index) => (
                <div 
                  key={index}
                  className="p-4 rounded-xl bg-void-800/30 border border-void-700/30"
                >
                  <h4 className="text-void-100 font-medium">{edu.institution}</h4>
                  <p className="text-void-500 text-sm">{edu.location}</p>
                  <p className="text-synth-400 text-sm mt-2">
                    {edu.degree || edu.focus}
                  </p>
                  <p className="text-void-500 text-sm">{edu.period}</p>
                  {edu.honors && (
                    <p className="text-amber-400 text-xs mt-2">{edu.honors}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.article>
        
        {/* Trust Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-synth-900/30 via-purple-900/20 to-ember-900/20 border border-synth-800/30 text-center"
        >
          <p className="text-void-300 leading-relaxed">
            Daniel's extensive experience across enterprise compliance, data architecture, and machine learning 
            forms the foundation of Graffiti™'s commitment to <span className="text-synth-400 font-medium">privacy</span>, 
            <span className="text-purple-400 font-medium"> security</span>, and 
            <span className="text-ember-400 font-medium"> accessibility</span> for all.
          </p>
        </motion.div>
      </div>
    </div>
  )
}